import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { KeyRound, Plus } from "lucide-react"
import { type FormEvent, useRef, useState } from "react"

import { api, ErrorApi } from "@/api/cliente"
import { Exportar } from "@/componentes/exportar/Exportar"
import { Boton } from "@/componentes/ui/Boton"
import { EstadoCarga, EstadoError, EstadoVacio } from "@/componentes/ui/Estados"
import { Insignia } from "@/componentes/ui/Insignia"
import { Modal } from "@/componentes/ui/Modal"
import { Tabla, Td, Th } from "@/componentes/ui/Tabla"
import { tienePermiso } from "@/lib/autorizacion"
import { LARGO_MINIMO_CLAVE as LARGO_MINIMO } from "@/lib/contrasenas"
import { comoFechaHora } from "@/lib/fechas"
import { usarSesion } from "@/lib/sesion"

const BASE = "/api/v1/agencia-transporte/intermediarios"

type Intermediario = {
  id: number
  nombre: string
  email: string
  documento: string | null
  telefono: string | null
  activo: boolean
  recibos: number
  ultimo_acceso: string | null
}

/**
 * Las cuentas del portal de transporte: una por intermediario. Sólo entran en
 * `transporte.<dominio>` y sólo ven lo suyo; aquí se crean, se les cambia la clave y se
 * desactivan.
 */
export function Intermediarios() {
  const cliente = useQueryClient()
  const usuario = usarSesion((e) => e.usuario)
  const administra = tienePermiso(usuario, "transporte:administrar")
  const [nuevo, setNuevo] = useState(false)
  const [clave, setClave] = useState<Intermediario | null>(null)
  const vista = useRef<HTMLDivElement | null>(null)
  const lista = useQuery({
    queryKey: ["agencia-transporte", "intermediarios"],
    queryFn: () => api.get<Intermediario[]>(BASE),
  })
  const activar = useMutation({
    mutationFn: (i: Intermediario) => api.post(`${BASE}/${i.id}/activo`, { activo: !i.activo }),
    onSuccess: () =>
      cliente.invalidateQueries({ queryKey: ["agencia-transporte", "intermediarios"] }),
  })

  if (lista.isPending) return <EstadoCarga etiqueta="Cargando intermediarios" />
  if (lista.isError)
    return (
      <EstadoError
        titulo="No se pudieron cargar"
        error={lista.error}
        onReintentar={() => lista.refetch()}
      />
    )

  return (
    <div className="space-y-3">
      {(administra || lista.data.length > 0) && (
        <div className="flex flex-wrap justify-end gap-2">
          {lista.data.length > 0 && (
            <Exportar
              titulo="Intermediarios de transporte"
              subtitulo="Cuentas del portal de transporte"
              contenido={vista}
            />
          )}
          {administra && (
            <Boton variante="primario" onClick={() => setNuevo(true)}>
              <Plus size={16} aria-hidden={true} /> Crear cuenta de intermediario
            </Boton>
          )}
        </div>
      )}
      {lista.data.length === 0 ? (
        <EstadoVacio
          titulo="Sin intermediarios"
          texto="Cree la cuenta de quien coordina los fletes para que emita sus recibos."
        />
      ) : (
        <div ref={vista}>
          <Tabla etiqueta="Intermediarios de transporte">
            <thead>
              <tr>
                <Th>Nombre</Th>
                <Th>Correo</Th>
                <Th>Documento / teléfono</Th>
                <Th className="text-right">Recibos</Th>
                <Th>Último acceso</Th>
                <Th>Estado</Th>
                {administra && <Th exportar={false}>Acciones</Th>}
              </tr>
            </thead>
            <tbody>
              {lista.data.map((i) => (
                <tr key={i.id}>
                  <Td>{i.nombre}</Td>
                  <Td>{i.email}</Td>
                  <Td>{[i.documento, i.telefono].filter(Boolean).join(" · ") || "—"}</Td>
                  <Td className="text-right tabular-nums">{i.recibos}</Td>
                  <Td className="tabular-nums">
                    {i.ultimo_acceso ? comoFechaHora(i.ultimo_acceso) : "Nunca"}
                  </Td>
                  <Td>
                    <Insignia tono={i.activo ? "exito" : "neutro"}>
                      {i.activo ? "Activa" : "Desactivada"}
                    </Insignia>
                  </Td>
                  {administra && (
                    <Td>
                      <span className="flex gap-1">
                        <Boton tamano="sm" variante="fantasma" onClick={() => setClave(i)}>
                          <KeyRound size={14} aria-hidden={true} /> Clave
                        </Boton>
                        <Boton
                          tamano="sm"
                          variante="fantasma"
                          disabled={activar.isPending}
                          onClick={() => activar.mutate(i)}
                        >
                          {i.activo ? "Desactivar" : "Activar"}
                        </Boton>
                      </span>
                    </Td>
                  )}
                </tr>
              ))}
            </tbody>
          </Tabla>
        </div>
      )}
      {nuevo && <Alta onCerrar={() => setNuevo(false)} />}
      {clave && <NuevaClave intermediario={clave} onCerrar={() => setClave(null)} />}
    </div>
  )
}

function Alta({ onCerrar }: { onCerrar: () => void }) {
  const cliente = useQueryClient()
  const [d, setD] = useState({ nombre: "", email: "", password: "", documento: "", telefono: "" })
  const crear = useMutation({
    mutationFn: () =>
      api.post(BASE, { ...d, documento: d.documento || null, telefono: d.telefono || null }),
    onSuccess: () => {
      cliente.invalidateQueries({ queryKey: ["agencia-transporte", "intermediarios"] })
      onCerrar()
    },
  })
  const enviar = (e: FormEvent) => {
    e.preventDefault()
    crear.mutate()
  }
  const campo = (k: keyof typeof d, t: string, tipo = "text") => (
    <label className="grid gap-1 text-sm">
      {t}
      <input
        className="campo"
        type={tipo}
        required={k === "nombre" || k === "email" || k === "password"}
        minLength={k === "password" ? LARGO_MINIMO : undefined}
        value={d[k]}
        onChange={(e) => setD({ ...d, [k]: e.target.value })}
      />
    </label>
  )
  return (
    <Modal
      abierto={true}
      titulo="Cuenta de intermediario"
      descripcion="Entrará en el portal de transporte con este correo."
      onCerrar={onCerrar}
    >
      <form onSubmit={enviar} className="grid gap-3">
        {campo("nombre", "Nombre y apellido *")}
        {campo("email", "Correo *", "email")}
        {campo("password", `Contraseña inicial * (mínimo ${LARGO_MINIMO})`, "password")}
        {campo("documento", "Cédula o RIF")}
        {campo("telefono", "Teléfono")}
        {crear.isError && (
          <p role="alert" className="text-sm" style={{ color: "var(--destructive)" }}>
            {crear.error instanceof ErrorApi ? crear.error.message : "No se pudo crear"}
          </p>
        )}
        <Boton
          type="submit"
          variante="primario"
          disabled={crear.isPending}
          className="justify-center"
        >
          Crear cuenta
        </Boton>
      </form>
    </Modal>
  )
}

function NuevaClave({
  intermediario,
  onCerrar,
}: {
  intermediario: Intermediario
  onCerrar: () => void
}) {
  const [password, setPassword] = useState("")
  const cambiar = useMutation({
    mutationFn: () => api.post(`${BASE}/${intermediario.id}/password`, { password }),
    onSuccess: onCerrar,
  })
  return (
    <Modal
      abierto={true}
      titulo={`Nueva contraseña de ${intermediario.nombre}`}
      onCerrar={onCerrar}
      ancho="sm"
    >
      <form
        className="grid gap-3"
        onSubmit={(e) => {
          e.preventDefault()
          cambiar.mutate()
        }}
      >
        <label className="grid gap-1 text-sm">
          Contraseña (mínimo {LARGO_MINIMO})
          <input
            className="campo"
            type="password"
            minLength={LARGO_MINIMO}
            required={true}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
        </label>
        {cambiar.isError && (
          <p role="alert" className="text-sm" style={{ color: "var(--destructive)" }}>
            {cambiar.error instanceof ErrorApi ? cambiar.error.message : "No se pudo cambiar"}
          </p>
        )}
        <Boton
          type="submit"
          variante="primario"
          disabled={cambiar.isPending}
          className="justify-center"
        >
          Guardar
        </Boton>
      </form>
    </Modal>
  )
}
